'use client';

import { createClient } from '@/lib/supabase/client';
import { AuditResult } from '@/app/lib/auditEngine';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sid = sessionStorage.getItem('friction_session_id');
  if (!sid) {
    sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem('friction_session_id', sid);
  }
  return sid;
}

export interface ScanRecord {
  id: string;
  url: string;
  overallScore: number;
  checks: any[];
  recommendations: any[];
  auditedAt: string;
  createdAt: string;
}

function rowToRecord(row: any): ScanRecord {
  return {
    id: row.id,
    url: row.url,
    overallScore: row.overall_score,
    checks: row.checks || [],
    recommendations: row.recommendations || [],
    auditedAt: row.audited_at,
    createdAt: row.created_at,
  };
}

export async function saveScan(result: AuditResult): Promise<string | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getOrCreateSessionId();

  const payload: any = {
    url: result.url,
    overall_score: result.overallScore,
    checks: result.checks,
    recommendations: result.recommendations,
    audited_at: result.auditedAt,
    session_id: sessionId,
    user_id: user?.id ?? null,
  };

  const { data, error } = await supabase
    .from('scan_history')
    .insert(payload)
    .select('id')
    .single();

  if (error) {
    console.error('saveScan error:', error.message);
    return null;
  }
  return data?.id ?? null;
}

export async function getScanHistory(): Promise<ScanRecord[]> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  const sessionId = getOrCreateSessionId();

  let query = supabase
    .from('scan_history')
    .select('id, url, overall_score, checks, recommendations, audited_at, created_at')
    .order('audited_at', { ascending: false })
    .limit(50);

  if (user) {
    query = query.eq('user_id', user.id);
  } else {
    query = query.eq('session_id', sessionId).is('user_id', null);
  }

  const { data, error } = await query;
  if (error) {
    console.error('getScanHistory error:', error.message);
    return [];
  }
  return (data || []).map(rowToRecord);
}

export async function getScanById(id: string): Promise<ScanRecord | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('scan_history')
    .select('id, url, overall_score, checks, recommendations, audited_at, created_at')
    .eq('id', id)
    .single();

  if (error) {
    console.error('getScanById error:', error.message);
    return null;
  }
  return data ? rowToRecord(data) : null;
}

export async function deleteScan(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('scan_history')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('deleteScan error:', error.message);
    return false;
  }
  return true;
}
